
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { PawPrint, MessageSquare, Share, Camera, AlertTriangle, Sparkles, Home as HomeIcon, Phone, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LikeButton from './LikeButton';
import PostCardActions from './PostCardActions';
import ShareModal from './ShareModal';
import CommentsModal from './CommentsModal';
import FollowButton from './FollowButton';
import MediaCarousel from './MediaCarousel';
import { MatchButton } from './MatchButton';

interface PostCardProps {
  post: {
    id: string;
    caption: string;
    created_at: string;
    media: { url: string; media_type?: string }[];
    pet: {
      id: string;
      name: string;
      photo_url?: string;
      user_id?: string;
      species?: string;
      is_memorial?: boolean;
    };
    likes_count: number;
    comments_count: number;
    user_has_liked: boolean;
    user_id?: string;
    post_type?: string;
    privacy_setting?: 'public' | 'private' | 'followers_only';
    contact_phone?: string | null;
    lost_lat?: number | null;
    lost_lng?: number | null;
    lost_address?: string | null;
    lost_last_seen_at?: string | null;
  };
}

const PostCard = ({ post }: PostCardProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const { t } = useTranslation();

  const isMemorial = post.pet.is_memorial === true;
  const isLost = post.post_type === 'lost_pet';
  const isMilestone = post.post_type === 'milestone';
  const isAdoption = post.post_type === 'adoption_post';

  // Themed wrapper per post_type
  const wrapperTone = isLost
    ? 'ring-2 ring-red-500/70 bg-red-50/40'
    : isMilestone
      ? 'ring-2 ring-amber-400/70 bg-amber-50/40'
      : isAdoption
        ? 'ring-2 ring-teal-500/70 bg-teal-50/40'
        : '';

  const renderBanner = () => {
    if (isLost) {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 text-white">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="text-sm font-bold uppercase tracking-wide">
            {t('postTypes.lostPet.banner', 'Lost Pet Alert')}
          </span>
        </div>
      );
    }
    if (isMilestone) {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
          <Sparkles className="h-4 w-4 shrink-0" />
          <span className="text-sm font-bold uppercase tracking-wide">
            {t('postTypes.milestone.banner', 'Milestone')}
          </span>
        </div>
      );
    }
    if (isAdoption) {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
          <HomeIcon className="h-4 w-4 shrink-0" />
          <span className="text-sm font-bold uppercase tracking-wide">
            {t('postTypes.adoption.banner', 'Available for Adoption')}
          </span>
        </div>
      );
    }
    if (post.post_type === 'profile_photo') {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700">
          <Camera className="h-4 w-4" />
          <span className="text-xs font-medium">{t('postTypes.profilePhoto.badge')}</span>
        </div>
      );
    }
    return null;
  };

  const renderLostActions = () => {
    if (!isLost) return null;
    const phone = (post.contact_phone || '').trim();
    const hasCoords = post.lost_lat != null && post.lost_lng != null;
    const waNumber = phone.replace(/[^\d+]/g, '').replace(/^\+/, '');
    const mapUrl = hasCoords
      ? `https://www.google.com/maps/search/?api=1&query=${post.lost_lat},${post.lost_lng}`
      : null;

    return (
      <div className="mx-4 mb-3 p-3 rounded-xl border border-red-200 bg-red-50/70 space-y-2">
        {post.lost_address && (
          <div className="flex items-start gap-2 text-sm text-red-900">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{post.lost_address}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {phone && (
            <>
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-semibold hover:bg-red-700"
              >
                <Phone className="h-3.5 w-3.5" />
                {t('postTypes.lostPet.call', 'Call')}
              </a>
              {waNumber && (
                <a
                  href={`https://wa.me/${waNumber}?text=${encodeURIComponent(
                    t('postTypes.lostPet.waPrefill', 'I have information about your lost pet')
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-600 text-white text-xs font-semibold hover:bg-green-700"
                >
                  <Phone className="h-3.5 w-3.5" />
                  WhatsApp
                </a>
              )}
            </>
          )}
          {mapUrl && (
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-100"
            >
              <MapPin className="h-3.5 w-3.5" />
              {t('postTypes.lostPet.viewMap', 'View on map')}
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`mb-8 md:rounded-lg md:overflow-hidden md:shadow-md ${isMemorial ? 'memorial-celebration-border' : ''} ${wrapperTone}`}>
      {renderBanner()}

      {/* Post Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <Link to={`/pet/${post.pet.id}`} className="flex items-center">
          <div className={`mr-2 ${isMemorial ? 'memorial-avatar-halo rounded-full' : ''}`}>
            <Avatar className="h-8 w-8">
              {post.pet.photo_url ? (
                <AvatarImage src={post.pet.photo_url} alt={post.pet.name} />
              ) : (
                <AvatarFallback className="bg-pet-purple/20 text-pet-purple">
                  <PawPrint className="h-4 w-4" />
                </AvatarFallback>
              )}
            </Avatar>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium hover:underline">{post.pet.name}</span>
              {isMemorial && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs italic text-white bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 whitespace-nowrap">
                  {t('common.foreverLoved')}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
            </span>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          {!isLost && (post.user_id || post.pet.user_id) && (
            <MatchButton
              petId={post.pet.id}
              petName={post.pet.name}
              petSpecies={post.pet.species}
              ownerId={post.user_id || post.pet.user_id}
              isMemorial={post.pet.is_memorial}
            />
          )}
          {(post.user_id || post.pet.user_id) && (
            <FollowButton targetUserId={post.user_id || post.pet.user_id!} />
          )}
        </div>
      </div>

      {/* Post Media */}
      {post.media.length > 0 && (
        <Link to={`/post/${post.id}`} className="block">
          <div className="relative w-full max-h-[75vh] overflow-hidden bg-gray-100 flex items-center justify-center">
            <MediaCarousel
              media={post.media}
              alt={post.caption || 'Post image'}
              containerClassName="max-h-[75vh]"
            />
          </div>
        </Link>
      )}

      {/* Lost Pet quick actions */}
      {renderLostActions()}

      {/* Post Caption */}
      {post.caption && (
        <div className="p-4 pt-2">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{post.caption}</p>
        </div>
      )}

      {/* Post Actions */}
      <div className="px-4 py-2 flex justify-between">
        <div className="flex items-center space-x-3">
          <LikeButton id={post.id} showModal={true} />
          <button
            type="button"
            onClick={() => setIsCommentsModalOpen(true)}
            className="flex items-center text-gray-500 hover:text-gray-700"
            aria-label={t('comments.openComments')}
          >
            <MessageSquare className="h-5 w-5 mr-1" />
            <span>{post.comments_count || 0}</span>
          </button>
          <button
            onClick={() => setIsShareModalOpen(true)}
            className="flex items-center text-gray-500 hover:text-gray-700"
          >
            <Share className="h-5 w-5 mr-1" />
            <span>{t('common.share', 'Share')}</span>
          </button>
        </div>
        <PostCardActions
          postId={post.id}
          petName={post.pet?.name}
          userId={post.user_id}
          caption={post.caption}
          privacySetting={post.privacy_setting}
        />
      </div>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        postId={post.id}
        petName={post.pet.name}
      />

      <CommentsModal
        isOpen={isCommentsModalOpen}
        onClose={() => setIsCommentsModalOpen(false)}
        postId={post.id}
        petName={post.pet?.name}
      />
    </div>
  );
};

export default PostCard;
